import * as i0 from '@angular/core';
import { Injectable, Inject, Optional, NgModule } from '@angular/core';
import * as i1 from '@tn-goldenfront/core/common';
import { DOCUMENT } from '@angular/common';
import * as i1$1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, mapTo, switchMap, map } from 'rxjs/operators';
import * as i2 from '@angular/platform-browser';

class SeoGtmService {
    constructor(seoSettings, platformService) {
        this.seoSettings = seoSettings;
        this.platformService = platformService;
        this.logging = this.seoSettings.gtm.logging;
    }
    /**
     * This method load the script gtm in the header of page
     *
     * @param gtmId The id from google tag manager
     */
    loadGTMById(gtmId = this.seoSettings.gtm.gtmId) {
        if (this.platformService.isBrowser && gtmId) {
            /* eslint-disable */
            // @ts-ignore
            window.dataLayer = window.dataLayer || [];
            (function (w, d, s, l, i) {
                w[l] = w[l] || [];
                w[l].push({
                    'gtm.start': new Date().getTime(),
                    event: 'gtm.js',
                });
                var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
                // @ts-ignore
                j.async = true;
                // @ts-ignore
                j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
                f.parentNode.insertBefore(j, f);
            })(window, document, 'script', 'dataLayer', gtmId);
        }
    }
    /**
     * This method is used to sent the information to dataLayer
     * @param dataLayer Object
     */
    pushToDataLayer(dataLayer) {
        if (this.platformService.isBrowser) {
            try {
                window.dataLayer.push(dataLayer);
            }
            catch (ex) {
                if (this.logging) {
                    console.error(ex);
                    console.error(`tracking failed - make sure you installed the scripts`);
                }
            }
        }
    }
    /**
     * Push an empty object in gtm
     */
    clean() {
        if (this.platformService.isBrowser) {
            this.pushToDataLayer({});
        }
    }
}
SeoGtmService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeoGtmService, deps: [{ token: 'seoSettings' }, { token: i1.PlatformService }], target: i0.ɵɵFactoryTarget.Injectable });
SeoGtmService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeoGtmService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeoGtmService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['seoSettings']
                }] }, { type: i1.PlatformService }]; } });

class SeoBaseService {
    constructor(router, _activatedRoute, _title, _meta, platformService, serverUrl, document, seoSettings) {
        this.router = router;
        this._activatedRoute = _activatedRoute;
        this._title = _title;
        this._meta = _meta;
        this.platformService = platformService;
        this.serverUrl = serverUrl;
        this.document = document;
        this.seoSettings = seoSettings;
        this.titleSubject = new Subject();
    }
    init() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd), mapTo(this._activatedRoute), switchMap((route) => {
            // TODO: https://github.com/angular/angular/issues/19420#issuecomment-333071573
            let childRoute = route;
            let child = childRoute.firstChild;
            while (child) {
                if (child.firstChild) {
                    child = child.firstChild;
                    childRoute = child;
                }
                else {
                    child = null;
                }
            }
            return childRoute.data;
        }), map((data) => ({
            data,
            url: this._activatedRoute.firstChild.snapshot['_routerState'].url,
        })))
            .subscribe((base) => {
            const canonical = base.data.canonicalUrl
                ? base.data.canonicalUrl
                : base.url;
            const serverURL = this.platformService.isServer
                ? this.serverUrl
                : `${window.location.protocol}//${window.location.host}`;
            // TODO: Quitar este subject(?)
            this.titleSubject.next(base.data.title);
            // set title
            if (base.data.title) {
                this._title.setTitle(base.data.title);
                this._meta.updateTag({
                    property: 'og:title',
                    content: base.data.title,
                });
            }
            // set description
            if (base.data.description) {
                this._meta.addTag({
                    name: 'description',
                    content: base.data.description,
                });
                this._meta.updateTag({
                    property: 'og:description',
                    content: base.data.description,
                });
            }
            // set robots
            if (base.data.robots) {
                this._meta.addTag({
                    name: 'robots',
                    content: base.data.robots,
                });
            }
            // set canonical
            this.createCanonicalURL(`${this.seoSettings.gtm.canonicalDomain}${canonical}`);
            if (base.data.shareImage) {
                this._meta.updateTag({
                    property: 'og:image',
                    content: base.data.shareImage,
                    itemprop: 'image',
                });
                this._meta.updateTag({
                    property: 'og:image:url',
                    content: base.data.shareImage,
                    itemprop: 'image',
                });
            }
            this._meta.updateTag({
                property: 'og:image:type',
                content: 'image/png',
            });
            this._meta.updateTag({
                property: 'og:url',
                content: `${serverURL}`,
            });
            this._meta.updateTag({
                property: 'og:type',
                content: 'website',
            });
        });
    }
    /**
     * Set canonical URL
     * Link: https://www.talkingdotnet.com/how-to-set-canonical-url-in-angular-7/
     */
    createCanonicalURL(path = null) {
        const url = path ? path : this.document.URL;
        const link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        this.document.head.appendChild(link);
        link.setAttribute('href', url);
    }
}
SeoBaseService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeoBaseService, deps: [{ token: i1$1.Router }, { token: i1$1.ActivatedRoute }, { token: i2.Title }, { token: i2.Meta }, { token: i1.PlatformService }, { token: 'serverUrl', optional: true }, { token: DOCUMENT }, { token: 'seoSettings' }], target: i0.ɵɵFactoryTarget.Injectable });
SeoBaseService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeoBaseService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeoBaseService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: i1$1.ActivatedRoute }, { type: i2.Title }, { type: i2.Meta }, { type: i1.PlatformService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['serverUrl']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['seoSettings']
                }] }]; } });

class NGFSeoModule {
    static forRoot(seoSettings) {
        return {
            ngModule: NGFSeoModule,
            providers: [
                {
                    provide: 'seoSettings',
                    useValue: seoSettings,
                },
            ],
        };
    }
}
NGFSeoModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NGFSeoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NGFSeoModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NGFSeoModule });
NGFSeoModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NGFSeoModule, providers: [SeoGtmService, SeoBaseService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NGFSeoModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    providers: [SeoGtmService, SeoBaseService],
                    exports: [],
                }]
        }] });

/*
 * Public API Surface of seo
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NGFSeoModule, SeoBaseService, SeoGtmService };
